import { useFeatureFlags } from "@/core/auth/hooks";
import { cameraApi } from "@/core/cameraApi";
import { CameraApiTagType } from "@/core/cameraApi/tagType";
import { bumpPipeline } from "@/modules/camera-stream/services/cameraPipelineGenerationSlice";
import { LayoutDashboard, TriangleAlert } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

export interface DashboardRuntimePageProps {
  configId: number;
  dashboardUrl: string | null;
}

export const DashboardRuntimePage = ({
  dashboardUrl,
}: DashboardRuntimePageProps) => {
  const { t } = useTranslation(["dashboard", "common"]);
  const dispatch = useDispatch();
  // useRunDeploy already nulls dashboardUrl for a restricted org, so the iframe
  // can't render — this only replaces the generic empty state with the reason.
  const { restricted } = useFeatureFlags();

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!dashboardUrl || event.origin !== new URL(dashboardUrl).origin) {
        return;
      }

      if (event.data?.type === "MODEL_DEPLOYED") {
        const { mxid, streamName } = event.data.data;
        dispatch(
          cameraApi.util.invalidateTags([
            { type: CameraApiTagType.NN, id: `${mxid}-${streamName}` },
            { type: CameraApiTagType.Dashboard, id: `${mxid}-${streamName}` },
            { type: CameraApiTagType.Streams, id: mxid },
          ]),
        );
        dispatch(bumpPipeline({ mxid, streamName }));
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [dashboardUrl]);

  if (restricted) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="mb-5 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-black/5 [&_svg]:size-7 [&_svg]:text-black/30">
          <TriangleAlert />
        </div>
        <p className="mb-2 text-base text-black/85">{t("runtime.restrictedTitle")}</p>
        <p className="max-w-[360px] text-center text-sm text-black/45">
          {t("runtime.restrictedBody")}
        </p>
      </div>
    );
  }

  if (dashboardUrl) {
    return (
      <iframe
        src={dashboardUrl}
        className="-m-4 h-[calc(100%+2rem)] w-[calc(100%+2rem)] border-none"
        allow="screen-wake-lock"
      />
    );
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center">
      <div className="mb-5 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-black/5 [&_svg]:size-7 [&_svg]:text-black/30">
        <LayoutDashboard />
      </div>
      <p className="mb-2 text-base text-black/85">{t("runtime.notRunningTitle")}</p>
      <p className="max-w-[360px] text-center text-sm text-black/45">
        {t("runtime.notRunningBody")}
      </p>
    </div>
  );
};
